import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { getAIModelConfig, getInvokeAIProvider } from '@advisor-core/lib/aiModelConfig';
import { AgGridReact } from 'ag-grid-react';
import { themeAlpine } from 'ag-grid-community';
import '@process-advisor/process-miner/shared/gridStyles.css';
import { renderDiscoveryRuleDashboardFromJSON } from '@process-advisor/process-miner/dashboard/discoveryRules/discoveryRule_renderer';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { openFindingForContext, setFindingContextFromGridRow } from '@process-advisor/process-miner/shared/findingContext';
import { v4 as uuidv4 } from 'uuid';
import Axios from 'axios';
import { X, ArrowUpRight, CheckCircle2, AlertTriangle } from 'lucide-react';
import { mapDiscoveryRuleToModelRule } from './discoveryRulePromotion';
import { getAppVersion, getIssueTypes, taskJSON, getSNOWApi } from '@process-advisor/process-miner/processMiner/processMiner';
import { AlertMessage } from '@process-advisor/process-miner/components/alerts/alert';
import { openProgressModal } from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal } from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { updateProgressModal } from '@process-advisor/process-miner/api/methods/modals/updateProgressModal';

import { processDiscoveryRulesMasterGridColumns } from '@process-advisor/process-miner/discoveryRules/discoveryRulesColumnsMaster';
import { processDiscoveryRulesDetailGridColumns } from '@process-advisor/process-miner/discoveryRules/discoveryRulesColumnsDetail';

// Asset Imports
import ClusterIcon from '@process-advisor/assets/clusterIcon.png';
import InfluencerIcon from '@process-advisor/assets/influencerIcon.png';
import DecomposeIcon from '@process-advisor/assets/decomposeIcon.png';
import DeleteIcon from '@process-advisor/assets/deleteIcon.png';
import EditIcon from '@process-advisor/assets/editIcon.png';
import TaskIcon from '@process-advisor/assets/taskIcon.png';
import AnalyticsIcon from '@process-advisor/assets/analyticsIcon.png';
import HistogramIcon from '@process-advisor/assets/histogramIcon.png';
import DetailIcon from '@process-advisor/assets/detailIcon.png';
import SaveIcon from '@process-advisor/assets/save.png';
import RefreshIcon from '@process-advisor/assets/refreshIcon.png';
import ExportIcon from '@process-advisor/assets/export.png';
import CSVIcon from '@process-advisor/assets/CSVIcon.png';
import ExcelIcon from '@process-advisor/assets/XLSIcon.png';
import RecommendIcon from '@process-advisor/assets/recommend.png';
import TaskLifeCycleIcon from '@process-advisor/assets/taskLifecycleIcon.png';
import { invokeProcessOpenAIWithBilling } from '@process-advisor/process-miner/consultant/aiBillingAccess';

// Styled Components
const Wrapper = styled.div`
  width: 100%;
  padding: 10px;
  height: 100%;
  min-height: 500px;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
`;

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderButton = styled.button`
  background: #0052cc;
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 3px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background .2s;
  &:hover { background: #0747a6; }
  &:disabled { opacity: .6; cursor: not-allowed; }
`;

const CloseButton = styled.button`
  padding: 6px;
  border: none;
  background: transparent;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover { background: #f3f4f6; }
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const Content = styled.div`
  padding: 20px;
  background: #f4f5f7;
  flex: 1;
  overflow: auto;
`;

const ExportSheet = styled.div`
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 8px 16px rgba(9,30,66,0.25);
  width: 440px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ExportHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #dfe1e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ExportTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #172b4d;
  margin: 0;
`;

const ExportBody = styled.div`
  padding: 16px 20px;
`;

const ExportLabel = styled.label`
  font-size: 14px;
  color: #172b4d;
  margin-bottom: 10px;
  display: block;
`;

const ExportOption = styled.button`
  width: 100%;
  text-align: left;
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 14px;
  border: 2px solid #dfe1e6;
  border-radius: 6px;
  margin-bottom: 10px;
  background: #fff;
  cursor: pointer;
  transition: all .2s;
  &:hover { border-color: #1868db; background: #f4f5f7; }
`;

const ExportIconSvg = styled.svg`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
`;

const ExportText = styled.div`
  display: flex;
  flex-direction: column;
`;

const ExportName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #172b4d;
`;

const ExportDesc = styled.span`
  font-size: 12px;
  color: #6b778c;
`;

const ExportFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #dfe1e6;
`;

const CancelButton = styled.button`
  padding: 8px 12px;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  &:hover { background: #f9fafb; }
`;

// Utility Functions
const noopModule = {
  invoke: () => Promise.resolve({ active: true, type: 'COMMERCIAL', items: [], result: [], customer_id: '' }),
  requestJira: () => Promise.resolve({
    json: () => Promise.resolve({ groups: { items: [] }, accountId: '', values: [], siteUrl: '' })
  }),
  view: { getContext: () => Promise.resolve({ siteUrl: '', localId: '' }) },
  router: { navigate: () => Promise.resolve() },
};
let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) {
    try {
      const mod = await import('@forge/bridge');
      forgeModule = (typeof mod.invoke === 'function') ? mod : noopModule;
    } catch(e) {
      forgeModule = noopModule;
    }
  }
  return forgeModule;
};

const cleanAndParseAIJson = (raw) => {
  let s = String(raw || '{}')
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();

  const first = s.indexOf('{');
  const last = s.lastIndexOf('}');
  if (first !== -1 && last !== -1 && last > first) s = s.slice(first, last + 1);

  try { return JSON.parse(s); } catch (err) {
    console.warn('[Discovery Rules AI] JSON parse failed; returning fallback payload', err, s.slice(0, 500));
    return {
      summary: s || 'The AI response could not be parsed as structured JSON.',
      kpis: {
        issue_type: '',
        affected_count: 0,
        affected_pct: 0,
        avg_duration_sec: 0,
        likely_primary_cause: 'Unable to parse AI response',
        key_signals: [],
      },
      observed_patterns: { attributes: [] },
      likely_primary_causes: [],
      root_cause_hypotheses: [],
      optimization_plan: {
        process_changes: [],
        jira_configuration: [],
        automation_rules: [],
        behavioural_nudges: [],
      },
      risks_and_mitigations: [],
      key_signals: [],
    };
  }
};

const getDirectGeminiKey = () => {
  try {
    if (typeof API_KEY !== 'undefined' && API_KEY) return API_KEY;
  } catch (_) {}
  return undefined;
};

const callDiscoveryRuleGemini = async ({ systemContent, userContent, model = getAIModelConfig().model }) => {
  const geminiModel = model && String(model).startsWith('gemini') ? model : getAIModelConfig().model;
  const key = getDirectGeminiKey();
  const prompt = [
    'Return valid JSON only. Do not wrap the response in markdown fences.',
    '',
    systemContent,
    '',
    userContent,
  ].join('\n');

  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${geminiModel}:generateContent?key=${key}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: { responseMimeType: 'application/json', maxOutputTokens: 32768 },
      }),
    }
  );

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Gemini request failed: ${res.status} ${res.statusText} ${body}`);
  }

  const data = await res.json();
  return cleanAndParseAIJson(data?.candidates?.[0]?.content?.parts?.[0]?.text || '{}');
};

const normaliseIssueTypesForPrompt = () => {
  try {
    const issueTypes = getIssueTypes();
    if (Array.isArray(issueTypes)) return issueTypes.filter(Boolean).map(String);
    if (typeof issueTypes === 'string' && issueTypes.trim()) return [issueTypes.trim()];
  } catch (_) {}
  try {
    const taskTable = typeof apiProvider.getTaskTable === 'function' ? apiProvider.getTaskTable() : null;
    if (taskTable?.label) return [taskTable.label];
    if (taskTable?.taskTable) return [taskTable.taskTable];
    if (taskTable?.table) return [taskTable.table];
    if (typeof taskTable === 'string' && taskTable.trim()) return [taskTable.trim()];
  } catch (_) {}
  return ['ServiceNow record'];
};

const numberWithCommas = (x) => {
  if (x === -1 || x === undefined) return '';
  return Number(x).toLocaleString();
};

const formatPercentage = (x, decimals) => {
  if (x === -1 || x === undefined) return '';
  return `${(x * 100).toFixed(decimals)}%`;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const traceExplorerDiscoveryRuleSave = (event, data = {}) => {
  const entry = { event, at: new Date().toISOString(), ...data };
  try { console.log('[PA Explorer Save Discovery Rule]', entry); } catch (_) {}
  try {
    window.__PA_EXPLORER_SAVE_DISCOVERY_RULE_TRACE__ = Array.isArray(window.__PA_EXPLORER_SAVE_DISCOVERY_RULE_TRACE__)
      ? window.__PA_EXPLORER_SAVE_DISCOVERY_RULE_TRACE__.slice(-99).concat(entry)
      : [entry];
    window.__PA_LAST_EXPLORER_SAVE_DISCOVERY_RULE_TRACE__ = entry;
  } catch (_) {}
  return entry;
};

const safeSaveRulePreview = (value, max = 1200) => {
  try {
    const text = JSON.stringify(value, null, 2);
    return text.length > max ? `${text.slice(0, max)}... [truncated]` : text;
  } catch (_) {
    return String(value);
  }
};

const boolInt = (value) => value === true || value === 1 || String(value || '').toLowerCase() === 'true' ? 1 : 0;

const cloneFiltersForGlobalRule = (rule = {}) => {
  const filters = Array.isArray(rule.filters)
    ? JSON.parse(JSON.stringify(rule.filters))
    : [];
  if (String(rule.type || '').toLowerCase() === 'discovery') {
    filters.forEach((filter) => {
      filter.steps = [];
      filter.activities = [];
      filter.variants = [];
    });
  }
  return filters;
};

const resolveGlobalRuleRecordType = (saveType) => {
  if (saveType !== 'issueType') return 'Generic';
  try {
    const issueTypes = typeof apiProvider.getIssueTypes === 'function' ? apiProvider.getIssueTypes() : null;
    if (Array.isArray(issueTypes) && issueTypes.length) return issueTypes.join(' | ');
    if (typeof issueTypes === 'string' && issueTypes.trim()) return issueTypes.trim();
  } catch (_) {}
  try {
    const taskTable = typeof apiProvider.getTaskTable === 'function' ? apiProvider.getTaskTable() : null;
    if (taskTable?.taskTable) return taskTable.taskTable;
    if (taskTable?.table) return taskTable.table;
    if (typeof taskTable === 'string' && taskTable.trim()) return taskTable.trim();
  } catch (_) {}
  return 'Generic';
};

const buildGlobalDiscoveryRulePayload = (discoveryRule = {}, saveType = 'generic') => {
  const id = uuidv4();
  const filters = cloneFiltersForGlobalRule(discoveryRule);
  const recordType = resolveGlobalRuleRecordType(saveType);

  return {
    id,
    discovery_rule: discoveryRule.discovery_rule,
    category: discoveryRule.category || 'Performance',
    type: discoveryRule.type || discoveryRule.discovery_rule_type || 'Discovery',
    discovery_rule_type: discoveryRule.discovery_rule_type || discoveryRule.type || 'Discovery',
    automation_opportunity: boolInt(discoveryRule.automation_opportunity),
    calculate_cost_impact: boolInt(discoveryRule.calculate_cost_impact),
    filters: JSON.stringify(filters),
    issue_type: recordType,
    record_type: recordType,
    activity_type: typeof apiProvider.getActivityType === 'function' ? apiProvider.getActivityType() : '',
    active: 'Y',
    save_variants: boolInt(discoveryRule.save_variants),
    save_activities: boolInt(discoveryRule.save_activities),
    save_steps: boolInt(discoveryRule.save_steps),
  };
};

const replaceQuotes = (obj) => {
  for (let key in obj) {
    if (typeof obj[key] === 'string') {
      obj[key] = obj[key].replace(/"([^"]+)"/g, "'$1'");
    } else if (obj[key] && typeof obj[key] === 'object') {
      replaceQuotes(obj[key]);
    }
  }
  return obj;
};

const EXCLUDE_ATTRS = new Set(['Work Item #', 'Duration']);

export function computeKpis(sampleItems, issueType, affectedCount, affectedPct) {
  const durations = sampleItems
    .map((r) => Number(r.Duration))
    .filter((v) => Number.isFinite(v) && v >= 0)
    .sort((a, b) => a - b);

  const avg = durations.length === 0 ? undefined : Math.round(durations.reduce((s, v) => s + v, 0) / durations.length);

  const med =
    durations.length === 0
      ? undefined
      : durations.length % 2
      ? durations[(durations.length - 1) / 2]
      : Math.round((durations[durations.length / 2 - 1] + durations[durations.length / 2]) / 2);

  return {
    issue_type: issueType,
    affected_count: affectedCount,
    affected_pct: affectedPct,
    avg_duration_sec: avg,
    median_duration_sec: med,
  };
}

export function inferObservedPatterns(sampleItems, topN = 3) {
  if (!sampleItems?.length) return { attributes: [] };

  const keys = Object.keys(sampleItems[0]).filter((k) => !EXCLUDE_ATTRS.has(k));
  const attributes = keys.map((attr) => {
    const freq = new Map();
    for (const row of sampleItems) {
      const val = row[attr] ?? 'N/A';
      freq.set(val, (freq.get(val) || 0) + 1);
    }
    const total = sampleItems.length;
    const top = [...freq.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, topN)
      .map(([val, cnt]) => `${val} (${((cnt / total) * 100).toFixed(1)}%)`)
      .join(', ');

    return {
      attribute: attr,
      observed_pattern: `Top ${Math.min(topN, freq.size)}: ${top}`,
      possible_interpretation: '',
    };
  });

  return { attributes };
}


// Component
export const DataGrid = ({ rows, customer_id, refreshDiscoveryRules, AppVersion }) => {
  try {
    if (typeof window !== 'undefined' && !window.__PA_EXPLORER_SAVE_DISCOVERY_RULE_GRID_RENDERED__) {
      window.__PA_EXPLORER_SAVE_DISCOVERY_RULE_GRID_RENDERED__ = true;
      traceExplorerDiscoveryRuleSave('explorer-grid-rendered', {
        appVersionProp: AppVersion,
        resolvedAppVersion: typeof getAppVersion === 'function' ? getAppVersion() : null,
        rowCount: Array.isArray(rows) ? rows.length : null,
        hasRefreshDiscoveryRules: typeof refreshDiscoveryRules === 'function',
        hasCustomerId: !!customer_id,
      });
    }
  } catch (_) {}

  const [detailColumnDefs, setDetailColumnDefs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [discoveryRuleText, setDiscoveryRuleText] = useState('');
  const [discoveryRule, setDiscoveryRule] = useState(null);
  const [insights, setInsights] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [promotion, setPromotion] = useState(null);
  const [promotionBusy, setPromotionBusy] = useState(false);
  const [promotionMessage, setPromotionMessage] = useState(null);

  const contentRef = useRef(null);
  const grid = useRef(null);
  const pollIntervalRef = useRef(null);
  const processingCtrRef = useRef(0);

  // Initialize column definitions
  useEffect(() => {
    setDetailColumnDefs(processDiscoveryRulesDetailGridColumns);
  }, []);

  // Render the dashboard when insights are updated
  useEffect(() => {
    if (insights && contentRef.current) {
      try {
        // Use the discoveryRule_renderer to render insights
        renderDiscoveryRuleDashboardFromJSON(insights, contentRef.current);
      } catch (e) {
        console.error('Render error:', e);
        contentRef.current.innerHTML = `<pre style="color:#d1242f">Failed to render insights.</pre>`;
      }
    }
  }, [insights]);

  // Polling for AI Insights
  useEffect(() => {
    if (requestId && (status?.status === 'pending' || status?.status === 'processing')) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const { invoke } = await loadForgeModule();
          const statusData = await invoke('checkStatus', { requestId });
          setStatus(statusData);
          updateProgressModal({ status: 'AI Insights - processing ' + processingCtrRef.current, ai: true  });
          processingCtrRef.current += 1;

          if (statusData.status === 'completed') {
            const json = JSON.parse(statusData.result);
            setInsights(json);
            setIsModalOpen(true);
            closeProgressModal();
            clearInterval(pollIntervalRef.current);
          } else if (statusData.status === 'failed') {
            setError(statusData.error || 'Request failed');
            closeProgressModal();
            clearInterval(pollIntervalRef.current);
          }
        } catch (err) {
          console.error('Error checking status:', err);
          setError('Failed to check status');
          closeProgressModal();
          clearInterval(pollIntervalRef.current);
        }
      }, 1000);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [requestId, status?.status]);

  const onRowClickedFindingContext = useCallback((params) => {
    setFindingContextFromGridRow(params?.data || params?.node?.data || {}, 'Discovery Rules', 'Discovery Rule');
  }, []);

  // Handle Escape Key for Modals
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === 'Escape') {
        if (isExportOpen) setIsExportOpen(false);
        else if (isModalOpen) setIsModalOpen(false);
      }
    };
    if (isModalOpen || isExportOpen) document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isModalOpen, isExportOpen]);

  // AI Insights Function
  const AI_Insights = async (rowDiscoveryRule) => {
    const discoveryRuleSchema = {
      name: 'DiscoveryRuleAnalysis',
      schema: {
        $schema: 'http://json-schema.org/draft-07/schema#',
        type: 'object',
        additionalProperties: false,
        properties: {
          summary: { type: 'string' },
          kpis: {
            type: 'object',
            additionalProperties: false,
            properties: {
              issue_type: { type: 'string' },
              affected_count: { type: 'integer' },
              affected_pct: { type: 'number' },
              avg_duration_sec: { type: 'number' },
              likely_primary_cause: { type: 'string' },
              key_signals: { type: 'array', items: { type: 'string' } },
            },
            required: [
              'issue_type',
              'affected_count',
              'affected_pct',
              'avg_duration_sec',
              'likely_primary_cause',
              'key_signals',
            ],
          },
          observed_patterns: {
            type: 'object',
            additionalProperties: false,
            properties: {
              attributes: {
                type: 'array',
                items: {
                  type: 'object',
                  additionalProperties: false,
                  properties: {
                    attribute: { type: 'string' },
                    observed_pattern: { type: 'string' },
                    possible_interpretation: { type: 'string' },
                  },
                  required: ['attribute', 'observed_pattern', 'possible_interpretation'],
                },
              },
            },
            required: ['attributes'],
          },
          likely_primary_causes: { type: 'array', items: { type: 'string' } },
          root_cause_hypotheses: {
            type: 'array',
            items: {
              type: 'object',
              additionalProperties: false,
              properties: {
                hypothesis: { type: 'string' },
                supporting_evidence: { type: 'string' },
              },
              required: ['hypothesis', 'supporting_evidence'],
            },
          },
          optimization_plan: {
            type: 'object',
            additionalProperties: false,
            properties: {
              process_changes: { type: 'array', items: { type: 'string' } },
              jira_configuration: { type: 'array', items: { type: 'string' } },
              automation_rules: {
                type: 'array',
                items: {
                  type: 'object',
                  additionalProperties: false,
                  properties: {
                    platform: { type: 'string' },
                    rule: { type: 'string' },
                  },
                  required: ['platform', 'rule'],
                },
              },
              behavioural_nudges: { type: 'array', items: { type: 'string' } },
            },
            required: ['process_changes', 'jira_configuration', 'automation_rules', 'behavioural_nudges'],
          },
          risks_and_mitigations: {
            type: 'array',
            items: {
              type: 'object',
              additionalProperties: false,
              properties: { risk: { type: 'string' }, mitigation: { type: 'string' } },
              required: ['risk', 'mitigation'],
            },
          },
          key_signals: { type: 'array', items: { type: 'string' } },
        },
        required: [
          'summary',
          'kpis',
          'observed_patterns',
          'likely_primary_causes',
          'root_cause_hypotheses',
          'optimization_plan',
          'risks_and_mitigations',
          'key_signals',
        ],
      },
      strict: true,
    };

    setDiscoveryRuleText(rowDiscoveryRule.discovery_rule);
    setDiscoveryRule(rowDiscoveryRule);
    setError(null);
    setStatus(null);
    setRequestId(null);

    const version = getAppVersion();
    await openProgressModal({ status: version === 'FORGE' ? 'AI Insights - queueing request' : 'Generating AI Discovery Rule Finding', ai: true });
    await sleep(50);

    const issueTypes = normaliseIssueTypesForPrompt();
    const issueTypeStr = issueTypes.length === 1 ? `Record Type = ${issueTypes[0]}` : `Record Types = ${issueTypes.join(' or ')}`;
    const issueTypeForKpi = issueTypes.join(' | ');

    const discoveryRuleTypeMap = {
      Performance: 'performance discovery rule',
      Compliance: 'compliance issue',
      Automation: 'automation opportunity',
    };
    const discoveryRuleTypeStr = discoveryRuleTypeMap[rowDiscoveryRule.category] || 'discovery rule';

    const sampleItems = taskJSON(); // array of work items/tasks in the current model context

    const systemContent =
      'You are an expert in ServiceNow and Jira workflow optimisation. ' +
      'Produce a concise, executive-grade analysis that EXACTLY conforms to the DiscoveryRuleAnalysis schema. ' +
      'Use the provided KPIs and dynamic attribute summaries; refine interpretations but do not invent attributes. ' +
      'For ServiceNow, use ServiceNow-native recommendations where relevant, such as Flow Designer, assignment rules, SLA definitions, notifications, queues, routing and knowledge/self-service improvements. ' +
      JSON.stringify(discoveryRuleSchema);

    const kpisBase = computeKpis(sampleItems, issueTypeForKpi, Number(rowDiscoveryRule.tasks || 0), Number(rowDiscoveryRule.percTasks || 0) * 100);
    const observed = inferObservedPatterns(sampleItems);

    const userContent = [
      `DiscoveryRule: ${rowDiscoveryRule.discovery_rule}`,
      `Application: ${version}`,
      issueTypeStr,
      `Affected: ${rowDiscoveryRule.tasks || 0} items (${((Number(rowDiscoveryRule.percTasks || 0)) * 100).toFixed(1)}%)`,
      `Events: ${rowDiscoveryRule.events || 0} (${((Number(rowDiscoveryRule.percEvents || 0)) * 100).toFixed(1)}%)`,
      `Average impact duration seconds: ${rowDiscoveryRule.avgImpactDurationTasks || 0}`,
      `Discovery rule category: ${rowDiscoveryRule.category || ''}`,
      `Discovery rule type: ${discoveryRuleTypeStr}`,
      '',
      'Sample (JSON array):',
      JSON.stringify(sampleItems || []),
      '',
      'Precomputed KPIs:',
      JSON.stringify(kpisBase),
      '',
      'Observed attribute summaries (dynamic):',
      JSON.stringify(observed),
    ].join('\n');

    try {
      if (typeof window !== 'undefined') {
        window.__PA_LAST_DISCOVERY_RULE_AI_DEBUG__ = {
          appVersion: version,
          row: rowDiscoveryRule,
          promptSize: systemContent.length + userContent.length,
          sampleRows: Array.isArray(sampleItems) ? sampleItems.length : 0,
          generatedAt: new Date().toISOString(),
        };
      }
    } catch (_) {}

    if (version === 'FORGE') {
      processingCtrRef.current = 1;
      try {
        const { invoke } = await loadForgeModule();
        const response = await invokeProcessOpenAIWithBilling({
          invoke,
          reportLabel: 'Discovery Rule AI',
          payload: { user: userContent, system: systemContent, aiProvider: getInvokeAIProvider(), model: getAIModelConfig().model, temperature: getAIModelConfig().temperature },
        });
        setRequestId(response.requestId);
        setStatus({ status: 'pending' });
      } catch (err) {
        console.error('Error submitting Discovery Rule AI request:', err);
        setError(err?.message || 'Failed to submit request');
        closeProgressModal();
      }
      return;
    }

    if (version === 'SNOW') {
      try {
        const parsed = await callDiscoveryRuleGemini({
          systemContent,
          userContent,
          model: getAIModelConfig().model,
        });
        replaceQuotes(parsed);
        setInsights(parsed);
        setIsModalOpen(true);
      } catch (err) {
        console.error('Error generating ServiceNow Discovery Rule AI finding:', err);
        setError(err?.message || 'Failed to generate insights. Please try again.');
      } finally {
        closeProgressModal();
      }
      return;
    }

    closeProgressModal();
    setError(`Unsupported app version: ${version || 'unknown'}`);
  };

  // Column Definitions
  const masterColumnDefs = useMemo(() => [
    {
      field: 'aiInsights',
      headerName: 'AI Insights',
      width: 130,
      suppressMenu: true,
      sortable: false,
      filter: false,
      resizable: false,
      cellRenderer: (params) => {
        const click = (e) => {
          e.stopPropagation();
          AI_Insights(params.data);
        };
        return (
          <div style={{ display: 'flex', height: '100%', cursor: 'pointer', paddingTop: 5, paddingLeft: 10 }} onClick={click} title="AI Insights">
            <svg width="50" height="32" viewBox="0 0 50 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="buttonGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#22d3ee" />
                  <stop offset="100%" stopColor="#A855F7" />
                </linearGradient>
              </defs>
              <rect width="50" height="32" rx="16" fill="url(#buttonGradient)" />
              <g transform="translate(13, 4) scale(1)">
                <path d="M12 2.5C8.4 2.5 5.5 5.4 5.5 9c0 2.4 1.3 4.5 3.2 5.6v1.9c0 .8.7 1.5 1.5 1.5h3.6c.8 0 1.5-.7 1.5-1.5v-1.9c1.9-1.1 3.2-3.2 3.2-5.6 0-3.6-2.9-6.5-6.5-6.5z" fill="#FFD54F" />
                <rect x="10.3" y="18" width="3.4" height="1.5" fill="#E65100" rx="0.7" />
                <circle cx="12" cy="8" r="2" fill="#FFFFFF" opacity="0.4" />
              </g>
            </svg>
          </div>
        );
      },
    },
    { field: 'discovery_rule_id', headerName: 'DiscoveryRule ID', hide: true },
    {
      colId: 'model_rule_status',
      headerName: 'Model rule',
      width: 118,
      minWidth: 118,
      maxWidth: 118,
      suppressMenu: true,
      sortable: true,
      filter: true,
      valueGetter: (params) => {
        const sourceId = String(params?.data?.discovery_rule_id || params?.data?.id || '');
        if (!sourceId || typeof window === 'undefined' || typeof window.__PA_GET_MODEL_RULES__ !== 'function') return '';
        const current = window.__PA_GET_MODEL_RULES__();
        return (current?.definitions || []).some((rule) => String(rule?.sourceDiscoveryRuleId || '') === sourceId) ? 'Promoted' : '';
      },
      cellRenderer: (params) => {
        if (params.value !== 'Promoted') return null;
        return (
          <span
            title="Promoted to a governed Process Model rule"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              marginTop: 7,
              padding: '2px 8px',
              borderRadius: 999,
              background: '#DCFFF1',
              color: '#216E4E',
              fontSize: 12,
              fontWeight: 700,
              lineHeight: '18px',
              whiteSpace: 'nowrap',
            }}
          >
            <CheckCircle2 size={13} strokeWidth={2.2} />
            Promoted
          </span>
        );
      },
    },
    {
      field: 'discovery_rule',
      headerName: 'DiscoveryRule',
      resizable: true,
      wrapText: true,
      autoHeight: true,
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'tasks',
      headerName: apiProvider.getEntityUpper() + 's',
      filter: 'agNumberColumnFilter',
      width: 140,
      type: 'rightAligned',
      valueFormatter: ({ value }) => numberWithCommas(value),
    },
    {
      field: 'percTasks',
      headerName: '% ' + apiProvider.getEntityUpper() + 's',
      filter: 'agNumberColumnFilter',
      width: 170,
      type: 'rightAligned',
      valueFormatter: ({ value }) => formatPercentage(value, 1),
      cellStyle: (params) => {
        const baseStyle = {
          fontSize: '14px',
          fontWeight: '600',
          textAlign: 'right',
        };
        if (params.value > 0.50) return { ...baseStyle, color: '#DE350B', backgroundColor: '#FFEBE6' };
        if (params.value > 0.20) return { ...baseStyle, color: '#FF8B00', backgroundColor: '#FFF4E6' };
        if (params.value > 0) return { ...baseStyle, color: '#00875A', backgroundColor: '#E3FCEF' };
        return { ...baseStyle, color: '#6B778C' };
      },
    },
    {
      field: 'events',
      headerName: 'Events',
      filter: 'agNumberColumnFilter',
      width: 100,
      type: 'rightAligned',
      valueFormatter: ({ value }) => numberWithCommas(value),
    },
    {
      field: 'percEvents',
      headerName: '% Events',
      filter: 'agNumberColumnFilter',
      width: 115,
      type: 'rightAligned',
      valueFormatter: ({ value }) => formatPercentage(value, 1),
      cellStyle: (params) => {
        const baseStyle = {
          fontSize: '14px',
          fontWeight: '600',
          textAlign: 'right',
        };
        if (params.value > 0.50) return { ...baseStyle, color: '#DE350B', backgroundColor: '#FFEBE6' };
        if (params.value > 0.20) return { ...baseStyle, color: '#FF8B00', backgroundColor: '#FFF4E6' };
        if (params.value > 0) return { ...baseStyle, color: '#00875A', backgroundColor: '#E3FCEF' };
        return { ...baseStyle, color: '#6B778C' };
      },
    },
    {
      field: 'avgImpactDurationTasks',
      headerName: 'Avg Impact Duration',
      filter: 'agNumberColumnFilter',
      width: 165,
      type: 'rightAligned',
      valueFormatter: ({ value }) => apiProvider.formatDuration({ seconds: value }),
    },
    { field: 'calculate_cost_impact', headerName: 'Calculate Cost Impact', filter: true, hide: true, width: 140 },
    {
      field: 'actual_cost',
      headerName: 'Actual Cost',
      hide: true,
      width: 100,
      aggFunc: 'sum',
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: ({ value }) => apiProvider.formatCost({ cost: value }),
    },
    {
      field: 'forecast_cost',
      headerName: 'Forecast Cost',
      hide: true,
      width: 100,
      aggFunc: 'sum',
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: ({ value }) => apiProvider.formatCost({ cost: value }),
    },
    {
      field: 'cost_saving',
      headerName: 'Cost Saving',
      hide: true,
      width: 100,
      aggFunc: 'sum',
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: ({ value }) => apiProvider.formatCost({ cost: value }),
    },
    {
      field: 'perc_cost_saving',
      headerName: '% Cost Saving',
      hide: true,
      width: 100,
      aggFunc: 'sum',
      filter: 'agNumberColumnFilter',
      type: 'rightAligned',
      valueFormatter: ({ value }) => formatPercentage(value, 2),
    },
    { field: 'id', headerName: 'ID', hide: true, suppressColumnsToolPanel: true },
    { field: 'save_activities', headerName: 'save_activities', hide: true, suppressColumnsToolPanel: true },
    { field: 'save_steps', headerName: 'save_steps', hide: true, suppressColumnsToolPanel: true },
    { field: 'save_variants', headerName: 'save_variants', hide: true, suppressColumnsToolPanel: true },
    { field: 'filter_activities', headerName: 'filter_activities', hide: true, suppressColumnsToolPanel: true },
    { field: 'filter_variants', headerName: 'filter_variants', hide: true, suppressColumnsToolPanel: true },
    { field: 'filter_steps', headerName: 'filter_steps', hide: true, suppressColumnsToolPanel: true },
    { field: 'discovery_rule_type', hide: true, suppressColumnsToolPanel: true },
    { field: 'discovery_rule_detail', hide: true, suppressColumnsToolPanel: true },
  ], []);

  // Save DiscoveryRule Type
  const saveDiscoveryRuleType = async (discoveryRule, saveType) => {
    traceExplorerDiscoveryRuleSave('action-start', {
      saveType,
      rowKeys: Object.keys(discoveryRule || {}),
      rowPreview: safeSaveRulePreview(discoveryRule),
    });

    openProgressModal({ status: 'Saving Discovery Rule' });
    await sleep(20);

    let payload = null;

    try {
      const appVersion = typeof getAppVersion === 'function' ? getAppVersion() : AppVersion;
      payload = buildGlobalDiscoveryRulePayload(discoveryRule || {}, saveType);

      traceExplorerDiscoveryRuleSave('payload-built', {
        appVersion,
        saveType,
        payloadPreview: safeSaveRulePreview(payload),
        discoveryRule: payload.discovery_rule,
        recordType: payload.record_type,
        activityType: payload.activity_type,
        filtersLength: String(payload.filters || '').length,
      });

      if (!payload.discovery_rule) {
        throw new Error('Discovery Rule text is missing on the selected row.');
      }

      if (appVersion === 'FORGE') {
        traceExplorerDiscoveryRuleSave('forge-create-start');
        const { invoke } = await loadForgeModule();
        const result = await invoke('createDiscoveryRule', { ...payload, customer_id });
        traceExplorerDiscoveryRuleSave('forge-create-success', { resultPreview: safeSaveRulePreview(result) });
      } else if (appVersion === 'SNOW') {
        traceExplorerDiscoveryRuleSave('snow-api-resolve-start');
        const snApi = await getSNOWApi();
        traceExplorerDiscoveryRuleSave('snow-api-resolved', {
          hasSnApi: !!snApi,
          discoveryMethods: snApi ? Object.keys(snApi).filter(k => String(k).toLowerCase().includes('discovery')).sort() : [],
          createDiscoveryRuleType: typeof snApi?.createDiscoveryRule,
        });

        if (!snApi || typeof snApi.createDiscoveryRule !== 'function') {
          throw new Error('ServiceNow API is not available for saving global Discovery Rules.');
        }

        traceExplorerDiscoveryRuleSave('snow-create-start', { payloadPreview: safeSaveRulePreview(payload) });
        const result = await snApi.createDiscoveryRule(payload);
        traceExplorerDiscoveryRuleSave('snow-create-success', { resultPreview: safeSaveRulePreview(result) });
      } else {
        throw new Error(`Unsupported app version: ${appVersion || 'unknown'}`);
      }

      if (typeof apiProvider.updateDiscoveryRuleID === 'function') {
        apiProvider.updateDiscoveryRuleID({ discovery_rule_id: discoveryRule.discovery_rule_id, id: payload.id });
      }
      if (typeof refreshDiscoveryRules === 'function') {
        refreshDiscoveryRules(true);
        traceExplorerDiscoveryRuleSave('refresh-called');
      } else if (typeof apiProvider.updateDiscoveryRules === 'function') {
        apiProvider.updateDiscoveryRules({});
        traceExplorerDiscoveryRuleSave('api-update-discovery-rules-called');
      } else {
        traceExplorerDiscoveryRuleSave('refresh-not-available');
      }

      if (typeof apiProvider.setDiscoveryRulesEdited === 'function') {
        apiProvider.setDiscoveryRulesEdited({ edited: true });
      }

      if (typeof apiProvider.alertMessage === 'function') {
        apiProvider.alertMessage({
          message: 'Discovery Rule saved',
          severity: 'success',
          showAlert: true,
          timeOutActive: true,
          component: 'pathConstraint',
        });
      }

      traceExplorerDiscoveryRuleSave('action-success');
    } catch (err) {
      traceExplorerDiscoveryRuleSave('action-error', {
        message: err?.message || String(err),
        stack: err?.stack || '',
        payloadPreview: safeSaveRulePreview(payload),
      });
      console.error('[PA Explorer Save Discovery Rule] failed', err);
      if (typeof apiProvider.alertMessage === 'function') {
        apiProvider.alertMessage({
          message: `Save Discovery Rule failed: ${err?.message || err}`,
          severity: 'error',
          showAlert: true,
          timeOutActive: true,
          component: 'pathConstraint',
        });
      }
    } finally {
      traceExplorerDiscoveryRuleSave('action-finish');
      closeProgressModal();
    }
  };

  // Context Menu
  const menuLabel = (symbol, label) => label;
  const menuIcon = (symbol) => `<span class="pa-ag-menu-icon">${symbol}</span>`;

  const getContextMenuItems = useCallback((params) => {
    traceExplorerDiscoveryRuleSave('context-menu-built', {
      rowKeys: Object.keys(params?.node?.data || {}),
      rowPreview: safeSaveRulePreview(params?.node?.data || {}),
    });
        const saveFindingMenuItem = {
            name: menuLabel('✚', 'Save Finding'), icon: menuIcon('✚'),
            action: function () {
                openFindingForContext(apiProvider, { source: 'Discovery Rules', objectType: 'Discovery Rule', row: params?.node?.data || {} });
            },
        };
    const process = [
      {
        name: 'Process',
        action: async () => {
          await openProgressModal({ status: 'Process DiscoveryRule' });
          await sleep(50);
          apiProvider.setAction_Visualizer({});
          apiProvider.processDiscoveryRuleDataGrid({ discovery_rule: params.node.data });
          closeProgressModal();
        },
        icon: `<img src="${RefreshIcon}" />`,
      },
    ];

    const download = [
      {
        name: 'Download',
        action: async () => {
          await openProgressModal({ status: 'Process and Download DiscoveryRule' });
          await sleep(50);
          await apiProvider.resetFilters({});
          await apiProvider.processDiscoveryRuleDataGrid({ discovery_rule: params.node.data });
          apiProvider.downloadDiscoveryRule({
            discovery_rule_id: params.node.data.id,
            discovery_rule: params.node.data.discovery_rule,
            filterSummary: params.node.data.filterSummary,
          });
          await apiProvider.resetFilters({});
          closeProgressModal();
          apiProvider.setAction_DiscoveryRules({});
        },
        icon: `<img src="${RecommendIcon}" />`,
      },
    ];

    const saveDiscoveryRule = [
      {
        name: 'Save Discovery Rule',
        icon: `<img src="${SaveIcon}" />`,
        subMenu: [
          {
            name: 'Work Type',
            action: () => {
              traceExplorerDiscoveryRuleSave('menu-click-work-type');
              return saveDiscoveryRuleType(params?.node?.data || {}, 'issueType');
            },
            icon: `<img src="${SaveIcon}" />`,
          },
          {
            name: 'Generic',
            action: () => {
              traceExplorerDiscoveryRuleSave('menu-click-generic');
              return saveDiscoveryRuleType(params?.node?.data || {}, 'generic');
            },
            icon: `<img src="${SaveIcon}" />`,
          },
        ],
      },
    ];

    const promotionCandidate = mapDiscoveryRuleToModelRule(params?.node?.data || {});
    const currentModelRules = typeof window !== 'undefined' && typeof window.__PA_GET_MODEL_RULES__ === 'function' ? window.__PA_GET_MODEL_RULES__() : {definitions:[]};
    const alreadyPromoted = promotionCandidate?.rule?.sourceDiscoveryRuleId && (currentModelRules?.definitions || []).some(r=>r.sourceDiscoveryRuleId===promotionCandidate.rule.sourceDiscoveryRuleId);
    const promoteToModelRule = {
      name: alreadyPromoted ? 'Promoted to model rule' : 'Promote to model rule',
      disabled: alreadyPromoted,
      icon: menuIcon(alreadyPromoted ? '✓' : '↗'),
      action: () => setPromotion({row:params?.node?.data || {},candidate:promotionCandidate}),
    };

    const insights = [
      {
        name: menuLabel('◆', 'Insights'), icon: menuIcon('◆'),
        icon: `<img src="${AnalyticsIcon}" />`,
        subMenu: [
          {
            name: menuLabel('●', 'Clusters'), icon: menuIcon('●'),
            action: async () => {
              await openProgressModal({ status: 'Processing Clusters' });
              await sleep(50);
              await apiProvider.generateTasksFilteredArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              await apiProvider.setDiagramCluster({ source: 'discovery_rule', discovery_rule_id: params.node.data.discovery_rule_id });
              closeProgressModal();
            },
            icon: `<img src="${ClusterIcon}" />`,
          },
          {
            name: menuLabel('↗', 'Influencers'), icon: menuIcon('↗'),
            action: async () => {
              await openProgressModal({ status: 'Processing Influencer Insights' });
              await sleep(50);
              await apiProvider.generateTasksFilteredArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              await apiProvider.setDiagramInfluenceAnalysis({ source: 'discovery_rule', discovery_rule_id: params.node.data.discovery_rule_id });
              closeProgressModal();
            },
            icon: `<img src="${InfluencerIcon}" />`,
          },
          {
            name: menuLabel('▦', 'Decompose'), icon: menuIcon('▦'),
            action: () => {
              apiProvider.generateTasksFilteredArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              apiProvider.setDiagramDecompositionTree({ source: 'discovery_rule' });
            },
            icon: `<img src="${DecomposeIcon}" />`,
          },
          {
            name: menuLabel('◷', 'Activity Analysis'), icon: menuIcon('◷'),
            action: () => {
              apiProvider.generateTasksFilteredArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              apiProvider.setTimeInActivitySource({ source: 'discovery_rule' });
              apiProvider.setAction_Time({});
            },
            icon: `<img src="${TaskLifeCycleIcon}" />`,
          },
          {
            name: menuLabel('▥', 'Histogram'), icon: menuIcon('▥'),
            action: () => {
              apiProvider.generateHistogramArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              apiProvider.histogram({});
            },
            icon: `<img src="${HistogramIcon}" />`,
          },
        ],
      },
      {
        name: `${apiProvider.getEntityUpper()}s`,
        icon: `<img src="${TaskIcon}" />`,
        subMenu: [
          {
            name: menuLabel('≡', 'Detail'), icon: menuIcon('≡'),
            action: () => {
              const discovery_rule_id = params.node.data.discovery_rule_id;
              apiProvider.setDiscoveryRuleDetailProps({ discoveryRule: discovery_rule_id });
              apiProvider.taskDetail({});
            },
            icon: `<img src="${DetailIcon}" />`,
          },
          {
            name: menuLabel('↻', 'History'), icon: menuIcon('↻'),
            action: () => {
              const discovery_rule_id = `f-${params.node.data.discovery_rule_id}`;
              apiProvider.processAnalytics({
                discovery_rule_id: discovery_rule_id,
                discovery_rule_type: params.node.data.category,
                object_type: 'DiscoveryRule',
                object_name: params.node.data.step,
                series_name: `${apiProvider.getEntityUpper()}s`,
              });
            },
            icon: `<img src="${HistogramIcon}" />`,
          },
          {
            name: menuLabel('⟳', 'LifeCycle'), icon: menuIcon('⟳'),
            action: () => {
              apiProvider.generateTasksFilteredArray({
                variantPaneVariants: false,
                type: 'discovery_rule',
                variant: 0,
                activity: 0,
                from: 0,
                to: 0,
                discovery_rule_id: params.node.data.discovery_rule_id,
              });
              apiProvider.setTasksVisualizationSource({ source: 'discovery_rule' });
              apiProvider.tasksVisualization();
              apiProvider.taskLifecycle({});
            },
            icon: `<img src="${TaskLifeCycleIcon}" />`,
          },
        ],
      },
      'separator',
      {
        name: 'Edit',
        action: () => {
          apiProvider.editDiscoveryRuleModal({
            discovery_rule_id: params.node.data.discovery_rule_id,
            discovery_rule: params.node.data.discovery_rule,
            category: params.node.data.category,
            calculate_cost_impact: params.node.data.calculate_cost_impact,
            automation_opportunity: params.node.data.automation_opportunity,
            type: params.node.data.type,
            discovery_rule_type: params.node.data.discovery_rule_type,
            tasks: params.node.data.tasks,
            events: params.node.data.events,
            filters: params.node.data.filters,
            filterSummary: params.node.data.filterSummary,
            save_activities: params.node.data.save_activities,
            save_steps: params.node.data.save_steps,
            save_variants: params.node.data.save_variants,
            filter_activities: params.node.data.filter_activities,
            filter_variants: params.node.data.filter_variants,
            filter_steps: params.node.data.filter_steps,
          });
          apiProvider.setDiscoveryRulesEdited({ edited: true });
        },
        icon: `<img src="${EditIcon}" />`,
      },
      {
        name: 'Delete',
        action: () => {
          apiProvider.deleteDiscoveryRule({ discovery_rule_id: params.node.data.discovery_rule_id });
          apiProvider.setDiscoveryRulesEdited({ edited: true });
        },
        icon: `<img src="${DeleteIcon}" />`,
      },
      'separator',
      {
        name: menuLabel('⇩', 'Export'), icon: menuIcon('⇩'),
        icon: `<img src="${ExportIcon}" />`,
        subMenu: [
          {
            name: menuLabel('CSV', 'CSV Export (all)'), icon: menuIcon('CSV'),
            action: () => params.api.exportDataAsCsv({ onlySelected: false }),
            icon: `<img src="${CSVIcon}" />`,
          },
          {
            name: menuLabel('CSV', 'CSV Export (selected)'), icon: menuIcon('CSV'),
            action: () => params.api.exportDataAsCsv({ onlySelected: true }),
            icon: `<img src="${CSVIcon}" />`,
          },
          {
            name: menuLabel('XLS', 'XLS Export (all)'), icon: menuIcon('XLS'),
            action: () => params.api.exportDataAsExcel({ onlySelected: false }),
            icon: `<img src="${ExcelIcon}" />`,
          },
          {
            name: menuLabel('XLS', 'XLS Export (selected)'), icon: menuIcon('XLS'),
            action: () => params.api.exportDataAsExcel({ onlySelected: true }),
            icon: `<img src="${ExcelIcon}" />`,
          },
        ],
      },
      'separator',
      'copy',
    ];

    let contextMenu = [saveFindingMenuItem, 'separator'].concat(process);
    if (params.node.data) {
      if (['parent', 'parent_discovery'].includes(params.node.data.discovery_rule_type)) {
        contextMenu = contextMenu.concat([promoteToModelRule]);
        contextMenu = contextMenu.concat(saveDiscoveryRule);
      }
      contextMenu = contextMenu.concat(download);
    }
    contextMenu = contextMenu.concat(insights);
    return contextMenu;
  }, [AppVersion, customer_id, refreshDiscoveryRules]);

  // Grid Event Handlers
  const onSaveGridColumnState = (params) => {
    const columnState = params.api.getColumnState();
    apiProvider.saveDiscoveryRulesGridState({ columnState });
  };

  const onFirstDataRendered = (params) => {
    const savedState = apiProvider.getDiscoveryRulesGridState();
    if (savedState) params.api.applyColumnState({ state: savedState });
  };

  const onFilterChanged = (params) => onSaveGridColumnState(params);
  const onColumnVisible = (params) => onSaveGridColumnState(params);
  const onColumnResized = (params) => onSaveGridColumnState(params);
  const onSortChanged = (params) => onSaveGridColumnState(params);
  const onRowClicked = (params) => {
    setFindingContextFromGridRow(params?.node?.data || {}, 'Discovery Rules', 'Discovery Rule');
    apiProvider.updateDiscoveryRuleDetail({
      discovery_rule_id: params.node.data.id,
      discovery_rule: params.node.data.discovery_rule,
      filterSummary: params.node.data.filterSummary,
    });
  };

  // Export Functions
  const download = (filename, mime, content) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const escapeHtml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const exportToHTML = () => {
    if (!insights || !contentRef.current) return;
    
    // Get the exact rendered HTML from the content div
    const renderedHTML = contentRef.current.innerHTML;
    
    // Get computed styles from the content element to preserve styling
    const computedStyles = window.getComputedStyle(contentRef.current);
    const contentStyles = `
      font-family: ${computedStyles.fontFamily};
      color: ${computedStyles.color};
      background: ${computedStyles.background};
      padding: ${computedStyles.padding};
    `;
    
    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${escapeHtml(discoveryRuleText || 'Analysis Report')}</title>
  <style>
    * {
      box-sizing: border-box;
    }
    body {
      font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
      margin: 0;
      padding: 24px;
      color: #172b4d;
    }
    h1 {
      font-size: 20px;
      margin: 0 0 20px;
      font-weight: 600;
    }
    .content {
      ${contentStyles}
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(discoveryRuleText || 'Analysis Report')}</h1>
  <div class="content">
    ${renderedHTML}
  </div>
</body>
</html>`;
    
    download(`${(discoveryRuleText || 'analysis').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.html`, 'text/html', html);
    setIsExportOpen(false);
  };

  const exportToJSON = () => {
    if (!insights) return;
    download(`${(discoveryRuleText || 'analysis').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`, 'application/json', JSON.stringify(insights, null, 2));
    setIsExportOpen(false);
  };

  const confirmPromotion = async () => {
    if (!promotion?.candidate?.ok) return;
    const promote = typeof window !== 'undefined' ? window.__PA_PROMOTE_DISCOVERY_RULE__ : null;
    if (typeof promote !== 'function') { setPromotionMessage({tone:'bad',text:'Model-rule promotion is not available in this runtime.'}); return; }
    setPromotionBusy(true); setPromotionMessage(null);
    try {
      const result=await promote(promotion.candidate.rule);
      setPromotionMessage({tone:'good',text:result?.alreadyPromoted?'This Discovery Rule was already promoted.':'Promoted and saved to this Process Model.'});
      grid.current?.api?.refreshCells?.({force:true});
      setTimeout(()=>setPromotion(null),650);
    } catch (e) { setPromotionMessage({tone:'bad',text:e?.message || 'Unable to promote this Discovery Rule.'}); }
    finally { setPromotionBusy(false); }
  };

  return (
    <Wrapper>
      <div
        className="ag-theme-alpine"
        style={{
          width: '100%',
          height: '100%',
          minHeight: '450px',
        }}
      >
        <AgGridReact
          theme={themeAlpine}
          ref={grid}
          masterDetail={true}
          rowSelection="single"
          headerHeight={45}
          columnDefs={masterColumnDefs}
          allowContextMenuWithControlKey={true}
          getContextMenuItems={getContextMenuItems}
          detailRowAutoHeight={true}
          overlayNoRowsTemplate={'No discoveryRules exist'}
          defaultColDef={{
            filter: true,
            resizable: true,
            sortable: true,
            wrapText: true,
            autoHeight: true,
          }}
          sideBar={{
            toolPanels: [
              {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                  suppressRowGroups: true,
                  suppressValues: true,
                  suppressPivotMode: true,
                },
              },
              {
                id: 'filters',
                labelDefault: 'Filters',
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel',
              },
            ],
            hiddenByDefault: false,
            defaultToolPanel: '',
          }}
          detailCellRendererParams={{
            detailGridOptions: {
              pagination: true,
              paginationAutoPageSize: true,
              suppressRowClickSelection: true,
              columnDefs: detailColumnDefs,
              getContextMenuItems,
            },
            getDetailRowData: (params) => {
              params.successCallback(params.data.discovery_ruleDetail);
            },
          }}
          onFirstDataRendered={onFirstDataRendered}
          onFilterChanged={onFilterChanged}
          onColumnVisible={onColumnVisible}
          onColumnResized={onColumnResized}
          onSortChanged={onSortChanged}
          onRowClicked={onRowClicked}
          rowData={rows}
        />
      </div>

      {isModalOpen && (
        <Overlay>
          <Modal>
            <Header>
              <ModalTitle>DiscoveryRule: {discoveryRuleText}</ModalTitle>
              <HeaderButtons>
                <HeaderButton onClick={() => AI_Insights(discoveryRule)} disabled={!discoveryRule}>Refresh</HeaderButton>
                <HeaderButton onClick={() => setIsExportOpen(true)} disabled={!insights}>Export…</HeaderButton>
                <CloseButton onClick={() => setIsModalOpen(false)} aria-label="Close dialog">
                  <X size={18} color="#6b7280" />
                </CloseButton>
              </HeaderButtons>
            </Header>
            <Content ref={contentRef} />
          </Modal>
        </Overlay>
      )}

      {isExportOpen && (
        <Overlay onClick={() => setIsExportOpen(false)}>
          <ExportSheet onClick={(e) => e.stopPropagation()}>
            <ExportHeader>
              <ExportTitle>Export Insights</ExportTitle>
              <CloseButton onClick={() => setIsExportOpen(false)}>
                <X size={18} color="#6b7280" />
              </CloseButton>
            </ExportHeader>
            <ExportBody>
              <ExportLabel>Choose your preferred export format:</ExportLabel>

              <ExportOption onClick={exportToHTML}>
                <ExportIconSvg viewBox="0 0 24 24" fill="#0052cc">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                </ExportIconSvg>
                <ExportText>
                  <ExportName>Export as HTML</ExportName>
                  <ExportDesc>Generate a printable HTML document</ExportDesc>
                </ExportText>
              </ExportOption>

              <ExportOption onClick={exportToJSON}>
                <ExportIconSvg viewBox="0 0 24 24" fill="#ff991f">
                  <path d="M3 5h18v2H3V5zm0 6h18v2H3v-2zm0 6h18v2H3v-2z" />
                </ExportIconSvg>
                <ExportText>
                  <ExportName>Export as JSON</ExportName>
                  <ExportDesc>Download raw data for further analysis</ExportDesc>
                </ExportText>
              </ExportOption>
            </ExportBody>
            <ExportFooter>
              <CancelButton onClick={() => setIsExportOpen(false)}>Cancel</CancelButton>
            </ExportFooter>
          </ExportSheet>
        </Overlay>
      )}


      {promotion && (
        <Overlay onClick={()=>!promotionBusy&&setPromotion(null)}>
          <ExportSheet onClick={(e)=>e.stopPropagation()} style={{width:560}}>
            <ExportHeader>
              <ExportTitle>Promote to model rule</ExportTitle>
              <CloseButton disabled={promotionBusy} onClick={()=>setPromotion(null)}><X size={18} color="#6b7280" /></CloseButton>
            </ExportHeader>
            <ExportBody>
              <div style={{fontSize:13,color:'#44546F',lineHeight:1.5,marginBottom:14}}>A promoted rule becomes a governed rule on this Process Model. The Discovery Rule remains independent in Explorer.</div>
              <div style={{fontWeight:700,color:'#172B4D',marginBottom:6}}>{promotion.row?.discovery_rule}</div>
              {promotion.candidate?.ok ? <>
                <div style={{padding:'10px 12px',background:'#F7F8F9',border:'1px solid #DFE1E6',borderRadius:6,fontSize:13,color:'#44546F'}}>
                  <div><b>Category:</b> {promotion.candidate.rule.category === 'compliance' ? 'Compliance' : 'Performance'}</div>
                  <div style={{marginTop:4}}><b>Mapped as:</b> {promotion.candidate.mapping}</div>
                  <div style={{marginTop:4}}><b>Initial status:</b> Active</div>
                </div>
              </> : <div style={{display:'flex',gap:9,padding:'10px 12px',background:'#FFF7F5',border:'1px solid #FFD5D2',borderRadius:6,color:'#AE2A19',fontSize:13}}><AlertTriangle size={18}/><span>{promotion.candidate?.reason}</span></div>}
              {promotionMessage&&<div style={{display:'flex',gap:8,marginTop:12,color:promotionMessage.tone==='bad'?'#AE2A19':'#216E4E',fontSize:13}}>{promotionMessage.tone==='bad'?<AlertTriangle size={17}/>:<CheckCircle2 size={17}/>}<span>{promotionMessage.text}</span></div>}
            </ExportBody>
            <ExportFooter>
              <CancelButton disabled={promotionBusy} onClick={()=>setPromotion(null)}>Cancel</CancelButton>
              {promotion.candidate?.ok&&<HeaderButton disabled={promotionBusy} onClick={confirmPromotion}><ArrowUpRight size={15}/>{promotionBusy?'Promoting…':'Promote & save'}</HeaderButton>}
            </ExportFooter>
          </ExportSheet>
        </Overlay>
      )}

      <AlertMessage />
    </Wrapper>
  );
};